import React, { useEffect } from 'react';
import { Form, Select } from 'antd';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import { useAuthStore } from '@stores/auth.store';
import { CreateRoleModalBody, CreateRoleModalFooter, ModalChromeHeading } from '@/pages/inside/tenant/style';
import { projectSprintTaskModalFormStyles } from '@/pages/inside/tenant/project/projectTabStyles';

function userAssignOptionLabel(u) {
    if (!u) return '';
    const s = (u.name ?? u.fullName ?? u.displayName ?? u.email ?? u.username ?? String(u.id ?? '')).trim();
    return s || String(u.id ?? '');
}

/**
 * Assign a sprint task to a project member. Data and submit handler come from {@link useProjectSprintsTab}.
 */
export default function SprintTaskAssignUserModal({ open, onCancel, task, users = [], onSubmit, submitPending }) {
    const [form] = Form.useForm();
    const currentUserId = useAuthStore((s) => s.user?.id ?? null);

    useEffect(() => {
        if (!open || !task) return;
        const raw = task.assigneeId ?? task.assignee_id ?? task.assignee?.id;
        const assigneeId = raw != null && raw !== '' ? String(raw) : undefined;
        const isSelf = currentUserId != null && assigneeId != null && String(assigneeId) === String(currentUserId);
        form.setFieldsValue({ assigneeId: isSelf ? undefined : assigneeId });
    }, [open, task, form, currentUserId]);

    const handleOk = () => {
        form.validateFields().then((values) => {
            onSubmit?.(values.assigneeId);
        });
    };

    const taskTitle = (task?.title ?? task?.name ?? 'Task').trim() || 'Task';

    const rawAssignee = task ? task.assigneeId ?? task.assignee_id ?? task.assignee?.id : null;
    const assignedToSelf =
        open &&
        task &&
        currentUserId != null &&
        rawAssignee != null &&
        String(rawAssignee) === String(currentUserId);

    const options = (Array.isArray(users) ? users : [])
        .filter((u) => u && u.id != null)
        .map((u) => ({
            value: String(u.id),
            label: userAssignOptionLabel(u),
        }));

    return (
        <AppModal
            size="sm"
            title={<ModalChromeHeading>Assign user</ModalChromeHeading>}
            open={open}
            onCancel={onCancel}
            footer={
                <CreateRoleModalFooter $inFooterSlot>
                    <Button $primary={false} $size="14px" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button
                        $primary
                        $blackOnLight
                        $size="14px"
                        loading={submitPending}
                        disabled={options.length === 0}
                        onClick={handleOk}
                    >
                        Assign
                    </Button>
                </CreateRoleModalFooter>
            }
        >
            <CreateRoleModalBody $flush $noMaxHeight>
                <Form form={form} layout="vertical" requiredMark={false} styles={projectSprintTaskModalFormStyles}>
                    <p style={{ margin: '0 0 12px', fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>{taskTitle}</p>
                    {assignedToSelf ? (
                        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                            You are currently assigned. Pick someone else to reassign.
                        </p>
                    ) : null}
                    {options.length === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: 'rgba(0,0,0,0.45)' }}>
                            No other project members available to assign.
                        </p>
                    ) : (
                        <Form.Item name="assigneeId" label="User" rules={[{ required: true, message: 'Select a user' }]}>
                            <Select
                                showSearch
                                placeholder="Choose user"
                                optionFilterProp="label"
                                options={options}
                                size="large"
                            />
                        </Form.Item>
                    )}
                </Form>
            </CreateRoleModalBody>
        </AppModal>
    );
}
